import { memoize } from 'lodash';
import { z } from 'zod';

import { HTTP_STATUS } from '@/common/http-status';
import { browserChatApiBase } from '@/lib/chat-agent-url';
import { fetchWithTimeout } from '@/lib/utils/api-client';
import { getHostname, isLocalhost, matchesDomain } from '@/lib/utils/domain';

const regionSchema = z.object({ label: z.string(), site: z.string(), language: z.string() });
const regionsBodySchema = z.object({ regions: z.array(z.unknown()) });

export type Region = z.infer<typeof regionSchema>;

/** `onError` hears a failed lookup once; the empty list is what the caller sees either way. */
type RegionsRequest = { tenant: string; onError: (error: Error) => void };

const OUR_DOMAIN = 'bazak.ai';
export const SITE_PARAM = 'site';

/** Host and path only: the agent picks its knowledge store by site, and a query string could
 *  carry a guest's personal details to every tool call. */
export function toSite(address: string): string | undefined {
  const withScheme = /^https?:\/\//i.test(address) ? address : `https://${address}`;
  try {
    const url = new URL(withScheme);
    if (!url.host) {
      return undefined;
    }
    return url.pathname === '/' ? url.host : `${url.host}${url.pathname}`;
  } catch {
    return undefined;
  }
}

/** A tenant page sends its own address. Our own pages send `?site=` when it names one, else the
 *  region in the interface language; with no such region, or no list, no site is sent and the
 *  agent answers from its default. */
export async function siteFor({
  tenant,
  locale,
  onError,
}: RegionsRequest & { locale: string }): Promise<string | undefined> {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const params = new URLSearchParams(window.location.search);
  const named = params.get(SITE_PARAM);
  return (
    tenantPageSite(params.get('parentUrl')) ??
    (named ? toSite(named) : undefined) ??
    regionSiteFor({ regions: await fetchRegions({ tenant, onError }), locale })
  );
}

/** The tenant website page the chat is embedded on. A page on our own domain is not one: there
 *  the interface language picks the region. The tenant's `targetDomains` can't decide this: it
 *  lists where links open, and a tenant embedded on a site it doesn't link to has none. */
function tenantPageSite(parentUrl: string | null): string | undefined {
  const hostname = parentUrl ? getHostname(parentUrl) : null;
  if (
    !parentUrl ||
    !hostname ||
    isLocalhost(hostname) ||
    matchesDomain({ hostname, pattern: OUR_DOMAIN })
  ) {
    return undefined;
  }
  return toSite(parentUrl);
}

function regionSiteFor({
  regions,
  locale,
}: {
  regions: Region[];
  locale: string;
}): string | undefined {
  const language = locale.toLowerCase().split('-')[0];
  return regions.find(region => region.language.toLowerCase() === language)?.site;
}

const REGIONS_TIMEOUT_MS = 5_000;

/** Asked once per page load: a language change reloads the page, so a failure (an empty list)
 *  is kept too rather than costing every later message the timeout. */
export const fetchRegions = memoize(loadRegionsOrNone, ({ tenant }) => tenant);

async function loadRegionsOrNone({ tenant, onError }: RegionsRequest): Promise<Region[]> {
  try {
    return await loadRegions(tenant);
  } catch (error) {
    onError(error instanceof Error ? error : new Error(String(error)));
    return [];
  }
}

async function loadRegions(tenant: string): Promise<Region[]> {
  // A signal rather than fetchWithTimeout's own timeout, which stops at the headers: the send path
  // awaits this list, so a body that never arrives would hold every message.
  const response = await fetchWithTimeout({
    url: `${browserChatApiBase()}/${tenant}/regions`,
    options: { signal: AbortSignal.timeout(REGIONS_TIMEOUT_MS) },
    retries: 0,
  });
  // TODO: drop once bazak-mastra#543 is on prod; after that a tenant without regions gets a 200
  // with an empty list, so a 404 is a misconfiguration worth reporting.
  if (response.status === HTTP_STATUS.NOT_FOUND) {
    return [];
  }
  assertOk({ response, tenant });
  const body = regionsBodySchema.safeParse(await response.json());
  return body.success ? body.data.regions.flatMap(parseRegion) : [];
}

function assertOk({ response, tenant }: { response: Response; tenant: string }): void {
  if (!response.ok) {
    throw new Error(`Region list refused with ${response.status} for ${tenant}`);
  }
}

function parseRegion(value: unknown): Region[] {
  const region = regionSchema.safeParse(value);
  return region.success ? [region.data] : [];
}
